import React, { useMemo } from 'react';
import { Lightbulb, Target } from 'lucide-react';

export default function InsightsPanel({ analytics, streakStats, sessions }) {
  const insights = useMemo(() => {
    const completedSessions = sessions?.filter(s => s?.status === 'completed');
    const recommendations = [];

    // Streak-based insights
    if (streakStats) {
      if (streakStats?.currentStreak >= 7) {
        recommendations?.push({
          icon: '🔥',
          type: 'achievement',
          title: 'Amazing Streak!',
          message: `You're on a ${streakStats?.currentStreak}-day streak! Keep up the momentum.`,
          priority: 'high'
        });
      } else if (streakStats?.currentStreak === 0 && streakStats?.totalSessions > 0) {
        recommendations?.push({
          icon: '⏰',
          type: 'reminder',
          title: 'Keep Your Streak Alive',
          message: 'Practice daily to build consistency and see faster improvement.',
          priority: 'medium'
        });
      }

      if (streakStats?.currentStreak > 0 && streakStats?.currentStreak === streakStats?.longestStreak) {
        recommendations?.push({
          icon: '🏆',
          type: 'milestone',
          title: 'New Personal Record!',
          message: `This is your longest streak ever at ${streakStats?.currentStreak} days!`,
          priority: 'high'
        });
      }
    }

    // Performance-based insights
    if (completedSessions?.length >= 3) {
      const recent3 = completedSessions?.slice(0, 3);
      const avgScore = recent3?.reduce((sum, s) => sum + s?.overallScore, 0) / 3;

      if (avgScore >= 85) {
        recommendations?.push({
          icon: '⭐',
          type: 'achievement',
          title: 'Excellent Performance',
          message: `Your recent average score is ${Math.round(avgScore)}%. You're mastering the fundamentals!`,
          priority: 'high'
        });
      } else if (avgScore < 60) {
        recommendations?.push({
          icon: '💪',
          type: 'improvement',
          title: 'Focus on Fundamentals',
          message: 'Consider starting with shorter sessions and building up gradually.',
          priority: 'medium'
        });
      }

      // Tool-specific insights
      const toolScores = { breath_coach: [], pitch_coach: [], audition_song: [] };
      completedSessions?.forEach(session => {
        session?.segments?.forEach(seg => {
          if (seg?.performanceScore > 0) {
            toolScores?.[seg?.toolType]?.push(seg?.performanceScore);
          }
        });
      });

      Object.entries(toolScores)?.forEach(([tool, scores]) => {
        if (scores?.length >= 3) {
          const avg = scores?.reduce((sum, s) => sum + s, 0) / scores?.length;
          const toolNames = {
            breath_coach: 'Breath Coach',
            pitch_coach: 'Pitch Coach',
            audition_song: 'Audition Song'
          };

          if (avg < 70) {
            recommendations?.push({
              icon: '🎯',
              type: 'improvement',
              title: `Focus on ${toolNames?.[tool]}`,
              message: `Your ${toolNames?.[tool]} average is ${Math.round(avg)}%. Try dedicating more time to this area.`,
              priority: 'medium'
            });
          }
        }
      });
    }

    // Time-based insights
    if (completedSessions?.length >= 5) {
      const totalMinutes = completedSessions?.reduce((sum, s) => sum + (s?.totalDurationSeconds || 0), 0) / 60;
      const avgMinutesPerSession = totalMinutes / completedSessions?.length;

      if (avgMinutesPerSession < 15) {
        recommendations?.push({
          icon: '⏱️',
          type: 'suggestion',
          title: 'Extend Practice Time',
          message: `Your sessions average ${Math.round(avgMinutesPerSession)} minutes. Aim for 20-30 minutes for optimal results.`,
          priority: 'low'
        });
      } else if (avgMinutesPerSession >= 30) {
        recommendations?.push({
          icon: '👍',
          type: 'achievement',
          title: 'Great Practice Duration',
          message: `You're averaging ${Math.round(avgMinutesPerSession)} minutes per session - ideal for skill development!`,
          priority: 'medium'
        });
      }
    }

    // Completion rate insights
    const avgCompletionRate = completedSessions?.length > 0
      ? completedSessions?.reduce((sum, s) => sum + s?.completionRate, 0) / completedSessions?.length
      : 0;

    if (avgCompletionRate < 80) {
      recommendations?.push({
        icon: '🎯',
        type: 'suggestion',
        title: 'Improve Completion Rate',
        message: 'Try shorter workflows or take breaks between segments to maintain focus.',
        priority: 'medium'
      });
    }

    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return recommendations?.sort((a, b) => priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority])?.slice(0, 5);
  }, [analytics, streakStats, sessions]);

  const getTypeColor = (type) => {
    const colors = {
      achievement: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-300',
      milestone: 'bg-purple-500/20 border-purple-500/30 text-purple-300',
      improvement: 'bg-blue-500/20 border-blue-500/30 text-blue-300',
      suggestion: 'bg-green-500/20 border-green-500/30 text-green-300',
      reminder: 'bg-red-500/20 border-red-500/30 text-red-300'
    };
    return colors?.[type] || colors?.suggestion;
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
        <Lightbulb className="w-6 h-6" />
        AI Insights & Tips
      </h2>
      {insights?.length === 0 ? (
        <div className="text-center py-8 text-white/60">
          <p>Complete more practice sessions to receive personalized insights and recommendations.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {insights?.map((insight, index) => (
            <div
              key={index}
              className={`p-4 rounded-xl border backdrop-blur-sm ${getTypeColor(insight?.type)}`}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">{insight?.icon}</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-1">
                    {insight?.title}
                  </h3>
                  <p className="text-sm text-white/80">
                    {insight?.message}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Quick Stats */}
      <div className="mt-6 pt-6 border-t border-white/20">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Stats</h3>
        <div className="space-y-3">
          {streakStats && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-white/70 text-sm">Total Practice Sessions</span>
                <span className="text-white font-semibold">{streakStats?.totalSessions}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/70 text-sm">Average Score</span>
                <span className="text-white font-semibold">{Math.round(streakStats?.avgScore)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/70 text-sm">Longest Streak</span>
                <span className="text-white font-semibold">{streakStats?.longestStreak} days</span>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Goals Section */}
      <div className="mt-6 pt-6 border-t border-white/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Target className="w-5 h-5" />
          Practice Goals
        </h3>
        <div className="space-y-3">
          <div className="p-3 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-sm">Weekly Sessions</span>
              <span className="text-white text-sm font-medium">
                {sessions?.filter(s => {
                  const weekAgo = new Date();
                  weekAgo?.setDate(weekAgo?.getDate() - 7);
                  return new Date(s.createdAt) > weekAgo;
                })?.length} / 5
              </span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2">
              <div
                className="bg-purple-500 h-full rounded-full transition-all"
                style={{ 
                  width: `${Math.min(100, (sessions?.filter(s => {
                    const weekAgo = new Date();
                    weekAgo?.setDate(weekAgo?.getDate() - 7);
                    return new Date(s.createdAt) > weekAgo;
                  })?.length / 5) * 100)}%` 
                }}
              />
            </div>
          </div>

          <div className="p-3 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-sm">Practice Streak</span>
              <span className="text-white text-sm font-medium">
                {streakStats?.currentStreak || 0} / 7 days
              </span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2">
              <div
                className="bg-green-500 h-full rounded-full transition-all"
                style={{ 
                  width: `${Math.min(100, ((streakStats?.currentStreak || 0) / 7) * 100)}%` 
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}